import EmptyState from "@/components/EmptyState";
import ImageWithSpinner from "@/components/ImageWithSpinner";
import MatchLoading from "@/components/MatchLoading";
import { Button } from "@/components/ui/button";
import { ColoredProgress } from "@/components/ui/colored-progress";
import { ApiError, PhotoType } from "@/generated-api";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";
import { useAiMatch, useGetProfileView } from "@/tanstack-hooks";
import {
  CloudOffIcon,
  HourglassIcon,
  // InfoIcon,
  SparklesIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function AiMatchPage() {
  const { data, isFetching, error, refetch } = useAiMatch();
  const isStandalone = useStandaloneMode();
  const navigate = useNavigate();

  const isRateLimited = (error as ApiError | null)?.status === 429;

  return (
    <div
      className={`flex flex-col dark w-full max-w-5xl mt-4 select-none px-2 gap-6 ${
        isStandalone ? "pb-24" : "pb-18"
      } mx-auto`}
    >
      <div className="flex flex-row w-full justify-between items-center">
        <div className="flex flex-row gap-2 items-center pb-5">
          <div className="text-xl font-semibold">Pulse AI</div>
          {/* <InfoIcon className="w-5 h-5 text-zinc-500" strokeWidth={2} /> */}
        </div>
        <div className="flex flex-col gap-1 items-center">
          <Button
            className="rounded-full gap-1"
            onClick={() => void refetch()}
            disabled={
              isFetching || isRateLimited || data?.remaining_today === 0
            }
          >
            <SparklesIcon
              className={`h-4 w-4 ${isFetching ? "animate-pulse" : undefined}`}
            />
            Next Match
          </Button>
          {(data || isRateLimited) && (
            <div className="text-xs text-zinc-500">
              {data?.remaining_today ?? 0} remaining today
            </div>
          )}
        </div>
      </div>

      {/* Any in-flight fetch — first load or a "Next Match" refetch — shows the
          loader. Once settled, a present match wins over the empty states, so a
          failed refetch falls back to the last match rather than an error state. */}
      {isFetching ? (
        <MatchLoading />
      ) : data ? (
        <>
          <MatchPhotoStack counterpartyId={data.counterparty_id} />

          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex flex-col gap-4 bg-zinc-900 rounded-3xl p-4 w-full max-w-xl">
              <div className="flex flex-row gap-4 items-center">
                <span className="font-semibold">Lifestyle overlap</span>
                <ColoredProgress
                  className="w-[50%]"
                  value={data.lifestyle_overlap * 10}
                />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <span className="font-semibold mr-1">Shared</span>
                {data.shared_attributes.map((attr) => (
                  <div
                    key={attr}
                    className="rounded-full items-center bg-[#353535] border border-[#474545] px-3 py-1"
                  >
                    {attr}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <span className="font-semibold mr-2">Complements</span>
                <HerBadge text={data.complements.her} />
                <HimBadge text={data.complements.him} />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <span className="font-semibold mr-2">Contrasts</span>
                <HerBadge text={data.contrasts.her} />
                <HimBadge text={data.contrasts.him} />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="text-xl font-bold ml-2">Analysis</div>
              <div className="bg-zinc-800 font-light w-full max-w-xl h-fit rounded-3xl p-4 shadow-[inset_2px_3px_4px_rgba(0,0,0,0.7)]">
                {data.paragraph}
              </div>
            </div>
          </div>

          <Button
            className="rounded-full w-[50%] mx-auto mt-2"
            onClick={() => navigate(`/profile/${data.counterparty_id}`)}
          >
            View Profile
          </Button>
        </>
      ) : isRateLimited ? (
        <EmptyState
          icon={HourglassIcon}
          title="That's all for today"
          message="You've used all your generated matches today. Check back tomorrow for a fresh set."
        />
      ) : (
        <EmptyState
          icon={CloudOffIcon}
          title="Couldn't load your match"
          message="Something went wrong finding your match. Please try again in a moment."
        />
      )}
    </div>
  );
}

function HimBadge({ text }: { text: string }) {
  return (
    <div className="rounded-full items-center bg-[#2C3663] border border-[#3D5280] px-3 py-1">
      {text}
    </div>
  );
}

function HerBadge({ text }: { text: string }) {
  return (
    <div className="rounded-full items-center bg-[#4E324E] border border-[#8B5184] px-3 py-1">
      {text}
    </div>
  );
}

function MatchPhotoStack({ counterpartyId }: { counterpartyId: string }) {
  const { data: profileView, isLoading } = useGetProfileView(counterpartyId);

  // The match is generated from the counterparty's public photos — mirror that here.
  const publicPhotos = (profileView?.photos ?? [])
    .filter((photo) => photo.photo_type === PhotoType.PUBLIC)
    .slice(0, 3);
  const stackItems = publicPhotos.length
    ? publicPhotos
    : isLoading
      ? [null, null, null]
      : [];

  return (
    <div className="flex items-start justify-center w-full max-w-2xl mx-auto">
      {stackItems.map((photo, i) => {
        // Scattered, lightly-overlapping stack: each photo sits at its own height
        // (middle dropped lowest), overlaps the previous by ~8%, rightmost on top.
        const dropTop = [0, 22, 8][i] ?? 0;
        return (
          <div
            key={photo ? photo.display_order : i}
            className="w-[38%] shrink-0 overflow-hidden rounded-2xl shadow-xl shadow-black/40"
            style={{
              marginLeft: i === 0 ? undefined : "-8%",
              marginTop: `${dropTop}%`,
              zIndex: i + 1,
            }}
          >
            {photo ? (
              <ImageWithSpinner src={photo.medium_url} className="" />
            ) : (
              <div className="aspect-[3/4] w-full animate-pulse bg-zinc-900" />
            )}
          </div>
        );
      })}
    </div>
  );
}
